<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import EnOverlayMenu from 'Components/navigation/EnOverlayMenu/EnOverlayMenu.vue';
import EnHelpIcon from 'Components/icons/EnHelpIcon.vue';
import { HelpLinks } from 'Js/constants/helpLinks';
import { useHelpMenuState } from 'Components/navigation/utils/useHelpMenuState';
import { useGuidedTour } from 'Js/utils/useGuidedTour';
import EnHelpMenuItem from './EnHelpMenuItem.vue';

/**
 * A button in the navbar used for helpful links and content for the user.
 */

const props = defineProps({
  subject: String,
});

const { t } = useI18n();
const { isOpen } = useHelpMenuState();

const guidedTourStepId = 'step-help';
const { active: isGuidedTourActive, currentStepId } = useGuidedTour();

const isPersistent = computed(() => {
  return isGuidedTourActive.value && currentStepId.value === guidedTourStepId;
});

function openEmail() {
  window.open(`mailto:${HelpLinks.SupportEmail}?Subject=${encodeURIComponent(props.subject ?? t('common.help.contactButtonSubject'))}`);
}

function openFaq() {
  window.open(HelpLinks.FAQ, '_blank');
}

function openDocumentation() {
  window.open(HelpLinks.Documentation, '_blank');
}

function openImprint() {
  window.open(HelpLinks.Imprint, '_blank');
}

function openPrivacyPolicy() {
  window.open(HelpLinks.PrivacyPolicy, '_blank');
}

watch(isPersistent, (persistent) => {
  if (!persistent && isOpen.value) {
    isOpen.value = false;
  }
}, { flush: 'post' });
</script>

<template>
  <div class="help-button" id="help-button">
    <en-overlay-menu :persistent="isPersistent" v-model="isOpen">
      <template #activator>
        <button class="help-menu-button">
          <en-help-icon></en-help-icon>
        </button>
      </template>

      <template #content>
        <div class="content-container" :data-guided-tour-step="guidedTourStepId">
          <div class="items-list p-2">
            <en-help-menu-item
                icon="mdi-email"
                id="email-item" 
                :label="t('common.help.writeEmailToCustomerServiceTooltip')"
                @click="openEmail">
            </en-help-menu-item>
            <en-help-menu-item
                icon="mdi-frequently-asked-questions"
                id="faq-item" 
                :label="t('common.help.frequentlyAskedQuestions')"
                @click="openFaq">
            </en-help-menu-item>
            <en-help-menu-item
                icon="mdi-text-box"
                id="documentation-item" 
                :label="t('common.help.documentation')"
                @click="openDocumentation">
            </en-help-menu-item>
          </div>

          <div class="en-datagrid-actions-menu-separator"></div>

          <div class="items-list p-2">
            <en-help-menu-item
                icon="mdi-certificate"
                id="certificate-item"
                :label="t('common.help.imprint')"
                @click="openImprint">
            </en-help-menu-item>
            <en-help-menu-item
                icon="mdi-information-slab-circle-outline"
                id="privacy-policy-item"
                :label="t('common.help.privacyPolicy')"
                @click="openPrivacyPolicy">
            </en-help-menu-item>
          </div>
        </div>
      </template>
    </en-overlay-menu>
  </div>
</template>


<style lang="scss" scoped>
@use "Styles/helper/variables.scss";

.items-list {
  font-size: 1rem;
}

.help-menu-button {
  display: flex;
}

.content-container {
  border-radius: variables.$notification-menu-border-radius;
  overflow: hidden;
}
</style>